/**
 * Mirror of the server-side manager permission catalogue
 * (xtreamer/server/constants/permissions.ts). Keep the string values in sync — the web app
 * gates UI on these, the server enforces the assignment/owner-level subset.
 */
export enum ManagerPermission {
  StreamStatuses = 'stream_statuses',
  StreamPreview = 'stream_preview',
  SetupChannels = 'setup_channels',
  StartStopStreams = 'start_stop_streams',
  ViewChat = 'view_chat',
  WriteChat = 'write_chat',
  SetupOverlays = 'setup_overlays',
  CreateUsers = 'create_users',
  DeleteUsers = 'delete_users',
  ChangeUserPlans = 'change_user_plans',
}

export const BUSINESS_USER_ROLE = {
  Streamer: 'streamer',
  Manager: 'manager',
} as const;

export const FREE_PLAN_PERMISSIONS: ManagerPermission[] = [
  ManagerPermission.StreamStatuses,
  ManagerPermission.StreamPreview,
];

export type ManagerPermissionGroup = {
  title: string;
  permissions: { value: ManagerPermission; label: string }[];
};

/**
 * Permissions grouped for display. Both the manager settings screen and the
 * create-user dialog render these, so the grouping stays in one place.
 */
export const MANAGER_PERMISSION_GROUPS: ManagerPermissionGroup[] = [
  {
    title: 'Preview',
    permissions: [
      { value: ManagerPermission.StreamStatuses, label: 'Stream statuses' },
      { value: ManagerPermission.StreamPreview, label: 'Live streams' },
      { value: ManagerPermission.ViewChat, label: 'Read chat' },
    ],
  },
  {
    title: 'Management',
    permissions: [
      { value: ManagerPermission.SetupChannels, label: 'Set up channels' },
      { value: ManagerPermission.StartStopStreams, label: 'Start/stop streams' },
      { value: ManagerPermission.WriteChat, label: 'Communicate in chats' },
      { value: ManagerPermission.SetupOverlays, label: 'Set up overlays' },
    ],
  },
  {
    title: 'Administrative',
    permissions: [
      { value: ManagerPermission.CreateUsers, label: 'Create users' },
      { value: ManagerPermission.DeleteUsers, label: 'Delete users' },
      { value: ManagerPermission.ChangeUserPlans, label: 'Change users plans' },
    ],
  },
];

/** Flat display order + labels, derived so there is a single source of truth. */
export const MANAGER_PERMISSION_LABELS: { value: ManagerPermission; label: string }[] =
  MANAGER_PERMISSION_GROUPS.flatMap(g => g.permissions);
